import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useContextResolution } from '../../contexts/ContextResolutionContext.js';
import {
  getUserTenants,
  bindGroup,
  getUserByLineIdToken,
  registerNewUser,
  createTenant,
} from '../../api/contextClient.js';
import { useLiff } from '../../contexts/LiffContext.js';
import type { UserTenantsResponse } from '@pos/shared/schemas';

/**
 * UnboundLanding 組件
 * 顯示未綁定群組的引導頁面，讓使用者可以綁定群組與 Tenant
 * 實作 Identity Resolution & Account Linking 流程
 */
export function UnboundLanding() {
  const navigate = useNavigate();
  const { lineGroupId, status, resolve } = useContextResolution();
  const { liff, isReady, isLoggedIn, shouldUseLiff } = useLiff();
  const [isLoading, setIsLoading] = useState(false);
  const [tenants, setTenants] = useState<UserTenantsResponse['tenants']>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<
    | 'checking'
    | 'unknown'
    | 'select'
    | 'binding'
    | 'linking'
    | 'registering'
    | 'creating'
  >('checking');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTenantName, setNewTenantName] = useState('');
  const [newTenantSlug, setNewTenantSlug] = useState('');
  const hasCheckedIdentity = useRef(false);

  // 若此群組已綁定某個商家，直接進入 Lite 主頁（一個群組最多一個商家綁定）
  useEffect(() => {
    if (status === 'bound' && lineGroupId) {
      navigate(`/lite?groupId=${encodeURIComponent(lineGroupId)}`, {
        replace: true,
      });
    }
  }, [status, lineGroupId, navigate]);

  // Phase 1: Detection - 檢查 LINE ID Token 對應的使用者
  useEffect(() => {
    if (!isReady || hasCheckedIdentity.current) {
      return;
    }

    // 檢查 URL 參數中是否有關聯成功的標記
    const urlParams = new URLSearchParams(window.location.search);
    const linkSuccess = urlParams.get('link_success');
    const linkError = urlParams.get('link_error');

    if (linkSuccess) {
      // 關聯成功，清除 URL 參數並重新檢查身份
      urlParams.delete('link_success');
      urlParams.delete('message');
      const newUrl =
        window.location.pathname +
        (urlParams.toString() ? `?${urlParams.toString()}` : '');
      window.history.replaceState({}, '', newUrl);
      hasCheckedIdentity.current = false; // 允許重新檢查
    }

    if (linkError) {
      // 關聯失敗，顯示錯誤訊息
      const errorMsg = urlParams.get('error') || '帳號關聯失敗';
      setError(errorMsg);
      urlParams.delete('link_error');
      urlParams.delete('error');
      const newUrl =
        window.location.pathname +
        (urlParams.toString() ? `?${urlParams.toString()}` : '');
      window.history.replaceState({}, '', newUrl);
    }

    // 檢查是否有 JWT Token（從 URL 或 localStorage）
    const jwtTokenFromUrl =
      urlParams.get('jwt_token') || urlParams.get('token');
    if (jwtTokenFromUrl) {
      localStorage.setItem('jwt_token', jwtTokenFromUrl);
      urlParams.delete('jwt_token');
      urlParams.delete('token');
      const newUrl =
        window.location.pathname +
        (urlParams.toString() ? `?${urlParams.toString()}` : '');
      window.history.replaceState({}, '', newUrl);
    }

    const jwtToken =
      jwtTokenFromUrl ||
      localStorage.getItem('jwt_token') ||
      sessionStorage.getItem('jwt_token');

    if (jwtToken) {
      // 已有 JWT Token，直接載入 Tenants
      setStep('select');
      loadTenantsWithJWT(jwtToken);
      return;
    }

    // 在 LIFF 環境中，使用 LINE ID Token 檢查身份
    if (liff && isLoggedIn) {
      checkIdentityWithLineToken();
    } else {
      // 非 LIFF 環境或未登入，使用傳統 Google 登入流程
      setStep('unknown');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, liff, isLoggedIn]);

  // 使用 LINE ID Token 檢查身份
  const checkIdentityWithLineToken = async () => {
    if (!liff || !isLoggedIn) {
      setStep('unknown');
      return;
    }

    setIsLoading(true);
    setError(null);
    setStep('checking');

    try {
      // 取得 LINE ID Token
      const lineIdToken = await liff.getIDToken();
      if (!lineIdToken) {
        throw new Error('無法取得 LINE ID Token');
      }

      console.log('🔍 檢查 LINE ID 身份...');

      // 發送 GET /api/lite/auth/me 檢查身份
      const result = await getUserByLineIdToken(lineIdToken);

      // 找到使用者，檢查是否有 Tenants
      setTenants(result.tenants);

      // 如果沒有 Tenants，直接顯示新增商店表單
      if (result.tenants.length === 0) {
        setStep('select');
        setShowCreateForm(true); // 直接顯示新增商店表單
        setError(null);
      } else {
        // 有 Tenants，進入選擇步驟
        setStep('select');
      }
    } catch (err) {
      if (err instanceof Error && err.message === 'USER_NOT_FOUND') {
        // LINE ID 不存在於系統中，這是正常情況（未綁定），顯示「我有帳號」選項
        // 不要使用 console.error，因為這是預期的正常流程
        console.log('ℹ️ 使用者不存在（未綁定），進入 unknown 狀態');
        setStep('unknown');
        setError(null); // 清除錯誤，因為這是正常情況
      } else {
        // 其他錯誤（網路錯誤、伺服器錯誤等）- 這些才是真正的錯誤
        console.error('❌ 身份檢查失敗:', err);
        setError(err instanceof Error ? err.message : '檢查身份時發生錯誤');
        setStep('unknown');
      }
    } finally {
      setIsLoading(false);
      hasCheckedIdentity.current = true;
    }
  };

  // 使用 JWT Token 載入 Tenants
  const loadTenantsWithJWT = async (jwtToken: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await getUserTenants(jwtToken);
      setTenants(result.tenants);
      setStep('select');

      // 如果沒有 Tenants，直接顯示新增商店表單
      if (result.tenants.length === 0) {
        setShowCreateForm(true); // 直接顯示新增商店表單
        setError(null);
      }
    } catch (err) {
      console.error('載入 Tenants 失敗:', err);
      setError(
        err instanceof Error ? err.message : '載入 Tenants 失敗，請稍後再試'
      );
      setStep('unknown'); // 載入失敗，回到未知步驟
      localStorage.removeItem('jwt_token'); // 清除無效的 token
    } finally {
      setIsLoading(false);
    }
  };

  // Phase 2: Authentication Challenge - 處理新用戶註冊
  const handleRegisterNewUser = async () => {
    console.log('[UnboundLanding] handleRegisterNewUser 被調用');
    // 在 Mock 模式下，即使 liff 或 isLoggedIn 為 false，也允許執行
    if (!shouldUseLiff || (!liff && !import.meta.env.VITE_ENABLE_LIFF_MOCK)) {
      setError('請在 LINE 環境中使用此功能');
      return;
    }

    setIsLoading(true);
    setError(null);
    setStep('registering');

    try {
      // 取得 LINE ID Token
      let lineIdToken: string | null = null;
      if (liff && isLoggedIn) {
        lineIdToken = await liff.getIDToken();
      } else if (import.meta.env.VITE_ENABLE_LIFF_MOCK === 'true') {
        // Mock 模式下生成假的 LINE ID Token
        lineIdToken = `mock_token_${localStorage.getItem('mockLineUserId') || 'U_MOCK_USER'}_${Date.now()}`;
        console.log(
          '[UnboundLanding] Mock LIFF 模式，生成假 LINE ID Token:',
          lineIdToken
        );
      }

      if (!lineIdToken) {
        throw new Error('無法取得 LINE ID Token');
      }

      console.log('✨ 註冊新用戶...');

      // 調用註冊 API
      // 注意：類型定義尚未更新，使用類型斷言
      const result = (await registerNewUser({ lineIdToken })) as unknown as {
        success: boolean;
        user: {
          id: string;
          email: string;
          name: string | null;
          avatarUrl: string | null;
        };
        tenant: {
          id: string;
          name: string;
        };
        jwtToken: string;
      };

      // 註冊成功，保存 JWT Token
      localStorage.setItem('jwt_token', result.jwtToken);

      // 註冊時已自動創建了 Lite User 和 Lite Tenant
      if (result.user && result.tenant) {
        console.log('✅ 註冊時已自動創建 Lite User:', result.user.id);
        console.log('✅ 註冊時已自動創建 Lite Tenant:', result.tenant.id);
      }

      // 載入 Tenants（註冊時應該已經自動創建了一個）
      try {
        const tenantsResult = await getUserTenants(result.jwtToken);
        if (tenantsResult.tenants.length > 0) {
          setTenants(tenantsResult.tenants);
          setStep('select');
          // 如果只有一個 Tenant，自動選中
          if (tenantsResult.tenants.length === 1) {
            setSelectedTenantId(tenantsResult.tenants[0].id);
          }
        } else {
          // 如果沒有 Tenants（理論上不應該發生，因為註冊時會自動創建）
          setError('您已成功註冊！但載入 Tenants 失敗，請重新整理頁面');
          setStep('select');
        }
      } catch (err) {
        // 如果載入 Tenants 失敗，檢查是否是 401 錯誤（需要 tenant_id）
        console.error('載入 Tenants 失敗:', err);
        if (
          err instanceof Error &&
          err.message.includes('TENANT_ID_REQUIRED')
        ) {
          // 這是因為 /api/users/me/tenants 需要 tenant_id，但這個 API 不應該需要
          // 先顯示錯誤，但讓用戶可以手動重新整理
          setError('載入 Tenants 時發生錯誤，請重新整理頁面重試');
        } else {
          setError('您已成功註冊！但載入 Tenants 失敗，請重新整理頁面');
        }
        setStep('select');
      }
    } catch (err) {
      console.error('❌ 註冊失敗:', err);
      setError(err instanceof Error ? err.message : '註冊失敗，請稍後再試');
      setStep('unknown');
    } finally {
      setIsLoading(false);
    }
  };

  // Phase 2: Authentication Challenge - 處理帳號關聯
  const handleLinkAccount = async () => {
    console.log('[UnboundLanding] handleLinkAccount 被調用', {
      liff: !!liff,
      isLoggedIn,
      isReady,
    });

    // 在 Mock 模式下，即使沒有 liff 或 isLoggedIn，也允許繼續
    const useMock = import.meta.env.VITE_ENABLE_LIFF_MOCK === 'true';

    if (!useMock && (!liff || !isLoggedIn)) {
      setError('請在 LINE 環境中使用此功能');
      return;
    }

    try {
      let lineIdToken: string | null = null;

      // 在 Mock 模式下，生成假的 LINE ID Token
      if (useMock) {
        lineIdToken = `mock_token_${Date.now()}`;
        console.log('[UnboundLanding] 使用 Mock LINE ID Token:', lineIdToken);
      } else {
        // 取得真實的 LINE ID Token
        if (!liff) {
          throw new Error('LIFF 未初始化');
        }
        lineIdToken = await liff.getIDToken();
        if (!lineIdToken) {
          throw new Error('無法取得 LINE ID Token');
        }
      }

      // 建立 Google OAuth 登入 URL（帶上 link_mode 和 line_id_token）
      const apiBase = import.meta.env.VITE_API_BASE_URL;
      if (!apiBase) {
        setError('API 位址未設定');
        return;
      }

      // 建立回調 URL（關聯完成後回到當前頁面）
      const currentUrl = window.location.href;
      const linkUrl = `${apiBase}/api/auth/google?link_mode=true&line_id_token=${encodeURIComponent(lineIdToken)}&redirect_uri=${encodeURIComponent(currentUrl)}`;

      console.log('🔗 開啟外部瀏覽器進行 Google 登入關聯...', { linkUrl });
      setStep('linking');

      // 使用 liff.openWindow 在外部瀏覽器開啟（Mock 模式下使用 window.open）
      if (useMock && liff) {
        liff.openWindow({
          url: linkUrl,
          external: true,
        });
      } else if (useMock) {
        // Mock 模式下，如果 liff 不存在，直接使用 window.open
        window.open(linkUrl, '_blank');
      } else if (liff) {
        liff.openWindow({
          url: linkUrl,
          external: true,
        });
      } else {
        throw new Error('無法開啟登入視窗');
      }
    } catch (err) {
      console.error('❌ 開啟關聯流程失敗:', err);
      setError(err instanceof Error ? err.message : '開啟關聯流程時發生錯誤');
      setStep('unknown');
    }
  };

  // Phase 4: Refresh - 關聯完成後重新檢查身份
  useEffect(() => {
    const handleFocus = () => {
      // 當頁面重新獲得焦點時，重新檢查身份
      if (hasCheckedIdentity.current && step === 'linking') {
        console.log('🔄 頁面重新獲得焦點，重新檢查身份...');
        hasCheckedIdentity.current = false;
        if (liff && isLoggedIn) {
          checkIdentityWithLineToken();
        }
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [step, liff, isLoggedIn]);

  // 處理手動重新檢查
  const handleRefresh = () => {
    hasCheckedIdentity.current = false;
    if (liff && isLoggedIn) {
      checkIdentityWithLineToken();
    } else {
      setStep('unknown');
    }
  };

  // 處理綁定
  const handleBind = async () => {
    if (!lineGroupId) {
      setError('無法取得群組 ID');
      return;
    }

    if (!selectedTenantId) {
      setError('請選擇一個 Tenant');
      return;
    }

    setIsLoading(true);
    setError(null);
    setStep('binding');

    try {
      // 從 localStorage 取得 JWT Token
      const jwtToken =
        localStorage.getItem('jwt_token') ||
        sessionStorage.getItem('jwt_token');

      if (!jwtToken) {
        throw new Error('請先使用 Google 登入');
      }

      // 執行綁定
      await bindGroup(
        {
          lineGroupId,
          tenantId: selectedTenantId,
        },
        jwtToken
      );

      // 綁定成功，整頁導向 Lite 主頁（讓 context 重新解析為已綁定後進入）
      const base = typeof window !== 'undefined' ? window.location.origin : '';
      window.location.assign(
        `${base}/lite?groupId=${encodeURIComponent(lineGroupId)}`
      );
      return;
    } catch (err) {
      console.error('綁定失敗:', err);
      setError(err instanceof Error ? err.message : '綁定失敗，請稍後再試');
      setStep('select');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4 py-8">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
        {/* Loading 狀態 */}
        {step === 'checking' && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mb-4"></div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              檢查身份中...
            </h2>
            <p className="text-sm text-gray-600">正在驗證您的 LINE 帳號</p>
          </div>
        )}

        {/* 非 Loading 狀態時顯示標題 */}
        {step !== 'checking' && (
          <div className="text-center mb-6">
            <div className="text-6xl mb-4">🔗</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {step === 'unknown'
                ? '找不到您的資料'
                : step === 'linking'
                  ? '正在關聯帳號'
                  : step === 'registering'
                    ? '註冊中...'
                    : step === 'select'
                      ? '選擇您的商店'
                      : step === 'binding'
                        ? '正在綁定中'
                        : '群組尚未綁定'}
            </h1>
            <p className="text-gray-600 text-sm">
              {step === 'unknown'
                ? '我們找不到您的資料，請選擇下一步操作'
                : step === 'linking'
                  ? '正在外部瀏覽器中進行 Google 登入...'
                  : step === 'registering'
                    ? '正在為您建立新帳號...'
                    : step === 'select'
                      ? '請選擇一個商店來綁定此 LINE 群組'
                      : step === 'binding'
                        ? '正在將群組綁定到您選擇的商店...'
                        : '此 LINE 群組尚未綁定任何商店，請先進行綁定以使用服務'}
            </p>
          </div>
        )}

        {error && (
          <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-400 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {step === 'unknown' && (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-800">
                💡
                提示：只有群組擁有者（Owner）可以進行綁定。如果您是群組擁有者，請使用「我有
                POS 帳號」選項進行綁定。
              </p>
            </div>

            <button
              onClick={handleLinkAccount}
              disabled={isLoading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg flex items-center justify-center space-x-2"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span>處理中...</span>
                </>
              ) : (
                <>
                  <span>🔐</span>
                  <span>我有 POS 帳號 (登入並關聯)</span>
                </>
              )}
            </button>

            <button
              onClick={handleRegisterNewUser}
              disabled={isLoading}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-4 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300 flex items-center justify-center space-x-2"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-600 border-t-transparent"></div>
                  <span>處理中...</span>
                </>
              ) : (
                <>
                  <span>✨</span>
                  <span>我是新用戶 (快速註冊)</span>
                </>
              )}
            </button>
          </div>
        )}

        {step === 'linking' && (
          <div className="text-center py-8 space-y-4">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600"></div>
            <div className="space-y-2">
              <p className="text-base font-medium text-gray-900">
                正在外部瀏覽器中進行 Google 登入...
              </p>
              <p className="text-sm text-gray-600">
                請在新開啟的瀏覽器視窗中完成 Google 登入
              </p>
              <p className="text-xs text-gray-500 mt-4">
                💡 完成登入後，請關閉外部瀏覽器，然後點擊下方按鈕回到 LINE 視窗
              </p>
            </div>
            <button
              onClick={handleRefresh}
              className="mt-6 w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors text-sm"
            >
              ✅ 已完成關聯，重新檢查
            </button>
          </div>
        )}

        {/* 選擇商店綁定：有商店則列出選擇，沒有則走建立流程；一個群組最多綁定一個商家 */}
        {step === 'select' && (
          <div className="space-y-4">
            {isLoading && tenants.length === 0 ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mb-4"></div>
                <p className="text-sm font-medium text-gray-900 mb-1">
                  載入中...
                </p>
                <p className="text-xs text-gray-600">正在取得您的商店列表</p>
              </div>
            ) : tenants.length === 0 ? (
              <div className="space-y-4">
                <div className="text-center py-6 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm font-medium text-yellow-800 mb-2">
                    ⚠️ 您目前沒有任何商店
                  </p>
                  <p className="text-xs text-yellow-700">
                    請先建立一個商店，然後再進行綁定
                  </p>
                </div>

                {!showCreateForm ? (
                  <button
                    onClick={() => setShowCreateForm(true)}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg flex items-center justify-center space-x-2"
                  >
                    <span>➕</span>
                    <span>新增商店</span>
                  </button>
                ) : (
                  <div className="space-y-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        商店名稱 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newTenantName}
                        onChange={e => setNewTenantName(e.target.value)}
                        placeholder="例如：我的商店"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        maxLength={100}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        商店識別碼 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newTenantSlug}
                        onChange={e => {
                          // 只允許小寫英數字、連字號和底線
                          const value = e.target.value
                            .toLowerCase()
                            .replace(/[^a-z0-9_-]/g, '');
                          setNewTenantSlug(value);
                        }}
                        placeholder="例如：my-shop"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        maxLength={50}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        只能包含小寫英數字、連字號(-)和底線(_)，長度 3-50 字元
                      </p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={async () => {
                          if (!newTenantName.trim()) {
                            setError('請輸入商店名稱');
                            return;
                          }
                          if (
                            !newTenantSlug.trim() ||
                            newTenantSlug.length < 3
                          ) {
                            setError('商店識別碼至少需要 3 個字元');
                            return;
                          }
                          if (!/^[a-z0-9_-]+$/.test(newTenantSlug)) {
                            setError(
                              '商店識別碼只能包含小寫英數字、連字號和底線'
                            );
                            return;
                          }

                          setIsLoading(true);
                          setError(null);
                          setStep('creating');

                          try {
                            const jwtToken =
                              localStorage.getItem('jwt_token') ||
                              sessionStorage.getItem('jwt_token');
                            if (!jwtToken) {
                              throw new Error('請先登入');
                            }

                            const result = await createTenant(
                              {
                                tenant_name: newTenantName.trim(),
                                slug: newTenantSlug.trim(),
                              },
                              jwtToken
                            );

                            // 重新載入 Tenants 列表
                            await loadTenantsWithJWT(jwtToken);

                            // 自動選中剛建立的商店
                            setSelectedTenantId(result.data.tenant_id);

                            // 關閉表單
                            setShowCreateForm(false);
                            setNewTenantName('');
                            setNewTenantSlug('');
                          } catch (err) {
                            console.error('建立商店失敗:', err);
                            setError(
                              err instanceof Error
                                ? err.message
                                : '建立商店失敗，請稍後再試'
                            );
                            setStep('select');
                          } finally {
                            setIsLoading(false);
                          }
                        }}
                        disabled={
                          isLoading ||
                          !newTenantName.trim() ||
                          !newTenantSlug.trim() ||
                          newTenantSlug.length < 3
                        }
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isLoading ? '建立中...' : '確認建立'}
                      </button>
                      <button
                        onClick={() => {
                          setShowCreateForm(false);
                          setNewTenantName('');
                          setNewTenantSlug('');
                          setError(null);
                        }}
                        disabled={isLoading}
                        className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-2 px-4 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        取消
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  <label className="block text-sm font-semibold text-gray-900 mb-3">
                    選擇要綁定的商店：
                  </label>
                  {tenants.map((tenant: UserTenantsResponse['tenants'][0]) => (
                    <label
                      key={tenant.id}
                      className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                        selectedTenantId === tenant.id
                          ? 'border-blue-500 bg-blue-50 shadow-md'
                          : 'border-gray-200 hover:border-gray-400 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="tenant"
                        value={tenant.id}
                        checked={selectedTenantId === tenant.id}
                        onChange={e => setSelectedTenantId(e.target.value)}
                        className="mr-4 h-4 w-4 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1 flex items-center space-x-3">
                        {tenant.logoUrl && (
                          <img
                            src={tenant.logoUrl}
                            alt={tenant.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        )}
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">
                            {tenant.name}
                          </div>
                          {tenant.slug && (
                            <div className="text-xs text-gray-500 mt-0.5">
                              {tenant.slug}
                            </div>
                          )}
                        </div>
                        {selectedTenantId === tenant.id && (
                          <div className="text-blue-600">
                            <svg
                              className="h-5 w-5"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
                <button
                  onClick={handleBind}
                  disabled={isLoading || !selectedTenantId}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg flex items-center justify-center space-x-2"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>處理中...</span>
                    </>
                  ) : (
                    <>
                      <span>✓</span>
                      <span>確認綁定</span>
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        )}

        {step === 'registering' && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mb-4"></div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              正在為您建立新帳號...
            </h2>
            <p className="text-sm text-gray-600">請稍候，這可能需要幾秒鐘</p>
          </div>
        )}

        {step === 'binding' && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mb-4"></div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              正在綁定中...
            </h2>
            <p className="text-sm text-gray-600">請稍候，這可能需要幾秒鐘</p>
          </div>
        )}
      </div>
    </div>
  );
}
